import React, { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";

import AgentGallery from "./ai/AgentGallery";
import { portfolioSrc, portfolioSrcSet } from "../lib/images";

const safeArray = (v) => (Array.isArray(v) ? v : []);

const ProjectPage = ({ resumeData }) => {
  const { slug } = useParams();

  // Scroll to top whenever the project changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const project = useMemo(() => {
    const projects = safeArray(resumeData?.portfolio?.projects);
    return projects.find((p) => p.slug === slug) || null;
  }, [resumeData, slug]);

  // Per-project document title and description, restored on unmount.
  useEffect(() => {
    if (!project) return undefined;

    const previousTitle = document.title;
    const meta = document.querySelector('meta[name="description"]');
    const previousDescription = meta ? meta.getAttribute("content") : null;

    document.title = `${project.title} | Tuan Nguyen`;
    if (meta && (project.cardSummary || project.tagline)) {
      meta.setAttribute("content", project.cardSummary || project.tagline);
    }

    return () => {
      document.title = previousTitle;
      if (meta && previousDescription !== null) {
        meta.setAttribute("content", previousDescription);
      }
    };
  }, [project]);

  if (!project) {
    return (
      <div className="project-page">
        <div className="row">
          <div className="twelve columns">
            <p className="project-back">
              <Link to="/#portfolio">← Back</Link>
            </p>
            <h1>Project not found</h1>
            <p>
              The project you're looking for doesn't exist (or hasn't been added
              yet).
            </p>
          </div>
        </div>
      </div>
    );
  }

  const coverFile = project.coverImage || project.image || null;
  // A diagram cover is composed to be read at card size; blown up full-bleed
  // behind the hero title it is just noise, so those pages get a plain hero.
  const isDiagramCover = Boolean(coverFile && /\.svg$/i.test(coverFile));
  const coverImage =
    coverFile && !isDiagramCover ? portfolioSrc(coverFile) : null;

  return (
    <div className="project-page">
      {/* Sticky Back Nav */}
      <nav className="project-back-bar ">
        <div className="row">
          <div className="twelve columns">
            <Link to="/#portfolio" className="project-back-link">
              ← Back to Portfolio
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="project-hero">
        {coverImage ? (
          <div
            className="project-hero-image"
            style={{ backgroundImage: `url("${coverImage}")` }}
            role="img"
            aria-label={`${project.title} cover`}
          />
        ) : null}

        <div className="row">
          <div className="twelve columns">
            {project.category ? (
              <p className="project-category-label">{project.category}</p>
            ) : null}
            <h1 className="project-title">{project.title}</h1>
            {project.tagline ? (
              <p className="project-tagline">{project.tagline}</p>
            ) : null}

            {/* Quick Stats Bar */}
            <div className="project-quick-stats">
              {project.role ? (
                <div className="project-stat">
                  <span className="project-stat-label">Role</span>
                  <span className="project-stat-value">{project.role}</span>
                </div>
              ) : null}
              {project.timeline ? (
                <div className="project-stat">
                  <span className="project-stat-label">Timeline</span>
                  <span className="project-stat-value">{project.timeline}</span>
                </div>
              ) : null}
              {project.team ? (
                <div className="project-stat">
                  <span className="project-stat-label">Team</span>
                  <span className="project-stat-value">{project.team}</span>
                </div>
              ) : null}
              {project.company ? (
                <div className="project-stat">
                  <span className="project-stat-label">Company</span>
                  <span className="project-stat-value">{project.company}</span>
                </div>
              ) : null}
            </div>

            {safeArray(project.links).length > 0 ? (
              <div className="project-links">
                {safeArray(project.links).map((l) => (
                  <a
                    key={l.url}
                    className="button project-link"
                    href={l.url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {l.label}
                  </a>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Live agents, directly under the hero.
          This is the reason someone opened the page, so it goes first — not
          eleven sections down behind the prose. */}
      {safeArray(project.agents).length > 0 ? (
        <div className="row">
          <div className="twelve columns">
            <AgentGallery agents={safeArray(project.agents)} />
          </div>
        </div>
      ) : null}

      {/* Executive Summary */}
      {project.summary || project.context ? (
        <div className="case-study-summary">
          <div className="row">
            <div className="twelve columns">
              <div className="summary-content">
                {project.summary ? (
                  <div className="summary-block">
                    <h2>Overview</h2>
                    <p className="summary-text">{project.summary}</p>
                  </div>
                ) : null}
                {project.context ? (
                  <div className="summary-block">
                    <h2>Context</h2>
                    <p className="summary-text">{project.context}</p>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Key Metrics / Impact */}
      {safeArray(project.metrics).length > 0 ? (
        <div className="case-study-metrics">
          <div className="row">
            <div className="twelve columns">
              <h2 className="section-heading">Key Impact</h2>
              <div className="metrics-grid">
                {safeArray(project.metrics).map((metric, i) => (
                  <div className="metric-card" key={i}>
                    <div className="metric-value">{metric.value}</div>
                    <div className="metric-label">{metric.label}</div>
                    {metric.description ? (
                      <div className="metric-description">{metric.description}</div>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Main Content Area */}
      <div className="case-study-content">
        <div className="row">
          {/* Left Column - Main Sections */}
          <div className="eight columns">
            {/* Problem Statement */}
            {project.problem ? (
              <section className="case-study-section">
                <div className="section-icon">
                  <i className="fa fa-exclamation-circle"></i>
                </div>
                <h2>The Challenge</h2>
                {typeof project.problem === 'string' ? (
                  <p>{project.problem}</p>
                ) : (
                  <>
                    {project.problem.description ? <p>{project.problem.description}</p> : null}
                    {safeArray(project.problem.points).length > 0 ? (
                      <ul className="case-study-bullets">
                        {safeArray(project.problem.points).map((p, i) => (
                          <li key={i}>{p}</li>
                        ))}
                      </ul>
                    ) : null}
                  </>
                )}
              </section>
            ) : null}

            {/* Solution / Approach */}
            {project.solution ? (
              <section className="case-study-section">
                <div className="section-icon">
                  <i className="fa fa-lightbulb-o"></i>
                </div>
                <h2>The Solution</h2>
                {typeof project.solution === 'string' ? (
                  <p>{project.solution}</p>
                ) : (
                  <>
                    {project.solution.description ? <p>{project.solution.description}</p> : null}
                    {safeArray(project.solution.points).length > 0 ? (
                      <ul className="case-study-bullets">
                        {safeArray(project.solution.points).map((p, i) => (
                          <li key={i}>{p}</li>
                        ))}
                      </ul>
                    ) : null}
                  </>
                )}
              </section>
            ) : null}

            {/* Process / Approach */}
            {project.process ? (
              <section className="case-study-section">
                <div className="section-icon">
                  <i className="fa fa-cogs"></i>
                </div>
                <h2>My Approach</h2>
                {typeof project.process === 'string' ? (
                  <p>{project.process}</p>
                ) : (
                  <>
                    {project.process.description ? <p>{project.process.description}</p> : null}
                    {safeArray(project.process.steps).length > 0 ? (
                      <div className="process-steps">
                        {safeArray(project.process.steps).map((step, i) => (
                          <div className="process-step" key={i}>
                            <div className="step-number">{i + 1}</div>
                            <div className="step-content">
                              <h4>{step.title}</h4>
                              <p>{step.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </>
                )}
              </section>
            ) : null}

            {/* Key Features */}
            {safeArray(project.features).length > 0 ? (
              <section className="case-study-section">
                <div className="section-icon">
                  <i className="fa fa-star"></i>
                </div>
                <h2>Key Features</h2>
                <div className="features-grid">
                  {safeArray(project.features).map((feature, i) => (
                    <div className="feature-card" key={i}>
                      {feature.icon ? (
                        <div className="feature-icon">
                          <i className={feature.icon}></i>
                        </div>
                      ) : null}
                      <h4>{feature.title}</h4>
                      <p>{feature.description}</p>
                    </div>
                  ))}
                </div>
              </section>
            ) : null}

            {/* Technical Implementation */}
            {project.technical ? (
              <section className="case-study-section">
                <div className="section-icon">
                  <i className="fa fa-code"></i>
                </div>
                <h2>Technical Implementation</h2>
                {typeof project.technical === 'string' ? (
                  <p>{project.technical}</p>
                ) : (
                  <>
                    {project.technical.description ? <p>{project.technical.description}</p> : null}
                    {safeArray(project.technical.decisions).length > 0 ? (
                      <div className="tech-decisions">
                        {safeArray(project.technical.decisions).map((decision, i) => (
                          <div className="tech-decision" key={i}>
                            <h4>{decision.title}</h4>
                            <p>{decision.description}</p>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </>
                )}
              </section>
            ) : null}

            {/* Challenges & Solutions */}
            {safeArray(project.challenges).length > 0 ? (
              <section className="case-study-section">
                <div className="section-icon">
                  <i className="fa fa-puzzle-piece"></i>
                </div>
                <h2>Challenges & Solutions</h2>
                <div className="challenges-list">
                  {safeArray(project.challenges).map((challenge, i) => (
                    <div className="challenge-item" key={i}>
                      <div className="challenge-problem">
                        <span className="challenge-label">Challenge</span>
                        <p>{challenge.problem}</p>
                      </div>
                      <div className="challenge-solution">
                        <span className="challenge-label">Solution</span>
                        <p>{challenge.solution}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ) : null}

            {/* Results / Outcomes */}
            {project.results ? (
              <section className="case-study-section case-study-results">
                <div className="section-icon">
                  <i className="fa fa-trophy"></i>
                </div>
                <h2>Results & Impact</h2>
                {typeof project.results === 'string' ? (
                  <p>{project.results}</p>
                ) : (
                  <>
                    {project.results.description ? <p>{project.results.description}</p> : null}
                    {safeArray(project.results.points).length > 0 ? (
                      <ul className="results-list">
                        {safeArray(project.results.points).map((r, i) => (
                          <li key={i}>{r}</li>
                        ))}
                      </ul>
                    ) : null}
                  </>
                )}
              </section>
            ) : null}

            {/* Learnings */}
            {project.learnings ? (
              <section className="case-study-section">
                  <div className="section-icon">
                  <i className="fa fa-trophy"></i>
                </div>
                <h2>Key Learnings</h2>
                {typeof project.learnings === 'string' ? (
                  <p>{project.learnings}</p>
                ) : (
                  <ul className="learnings-list">
                    {safeArray(project.learnings).map((l, i) => (
                      <li key={i}>{l}</li>
                    ))}
                  </ul>
                )}
              </section>
            ) : null}

            {/* Legacy Sections (for backwards compatibility) */}
            {safeArray(project.sections).map((section) => (
              <section className="case-study-section" key={section.title}>
                <h2>{section.title}</h2>
                {safeArray(section.paragraphs).map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
                {safeArray(section.bullets).length > 0 ? (
                  <ul className="case-study-bullets">
                    {safeArray(section.bullets).map((b, i) => (
                      <li key={i}>{b}</li>
                    ))}
                  </ul>
                ) : null}
              </section>
            ))}
          </div>

          {/* Right Column - Sidebar */}
          <aside className="four columns case-study-sidebar">
            {/* Tech Stack */}
            {safeArray(project.stack).length > 0 ? (
              <div className="sidebar-card">
                <h3>Tech Stack</h3>
                <div className="project-pill-row">
                  {safeArray(project.stack).map((t) => (
                    <span className="project-pill" key={t}>
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}

            {/* My Responsibilities */}
            {safeArray(project.responsibilities).length > 0 ? (
              <div className="sidebar-card">
                <h3>My Responsibilities</h3>
                <ul className="sidebar-list">
                  {safeArray(project.responsibilities).map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {/* Highlights */}
            {safeArray(project.highlights).length > 0 ? (
              <div className="sidebar-card">
                <h3>Highlights</h3>
                <ul className="sidebar-list highlights-list">
                  {safeArray(project.highlights).map((h, i) => (
                    <li key={i}>{h}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {/* Screenshots/Gallery */}
            {safeArray(project.gallery).length > 0 ? (
              <div className="sidebar-card">
                <h3>Screenshots</h3>
                <div className="project-gallery">
                  {safeArray(project.gallery).map((img) => (
                    <img
                      key={img}
                      src={portfolioSrc(img)}
                      srcSet={portfolioSrcSet(img)}
                      sizes="(min-width: 900px) 300px, 92vw"
                      alt={`${project.title} screenshot`}
                      loading="lazy"
                    />
                  ))}
                </div>
              </div>
            ) : null}

            {/* Links */}
            {safeArray(project.links).length > 0 ? (
              <div className="sidebar-card">
                <h3>Links</h3>
                <div className="sidebar-links">
                  {safeArray(project.links).map((l) => (
                    <a
                      key={l.url}
                      className="sidebar-link"
                      href={l.url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {l.label} <i className="fa fa-external-link"></i>
                    </a>
                  ))}
                </div>
              </div>
            ) : null}
          </aside>
        </div>
      </div>

      {/* Call to Action */}
      <div className="case-study-cta">
        <div className="row">
          <div className="twelve columns">
            <h3>Interested in working together?</h3>
            <p>I'm always open to discussing new projects and opportunities.</p>
            <div className="cta-buttons">
              <Link to="/#contact" className="button cta-primary">
                Get in Touch
              </Link>
              <Link to="/#portfolio" className="button cta-secondary">
                View More Projects
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectPage;
